import type { ContentAsset } from '@zuruflex/types';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Skeleton,
  cn,
} from '@zuruflex/ui';
import { useQuery } from '@tanstack/react-query';
import { FileText } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api.js';

export interface MediaLibraryProps {
  onPick: (asset: ContentAsset) => void;
  onClose: () => void;
}

const size = (bytes: number) =>
  bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

/**
 * Everything already uploaded for pages, so a picture used on one page is
 * not uploaded again for the next. The bucket is the list — nothing here
 * can drift from what is actually stored.
 */
export function MediaLibrary({ onPick, onClose }: MediaLibraryProps) {
  const { t } = useTranslation(['admin', 'common']);
  const [filter, setFilter] = useState<'all' | 'image' | 'pdf'>('all');
  const { data, isPending, error } = useQuery({
    queryKey: ['content', 'assets'],
    queryFn: () => api.contentAssets(),
    // A fresh upload should be there the next time this opens.
    staleTime: 0,
  });

  const shown = (data ?? []).filter((asset) => filter === 'all' || asset.kind === filter);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('admin:content.library.title')}</DialogTitle>
          <DialogDescription>{t('admin:content.library.body')}</DialogDescription>
        </DialogHeader>

        <div className="flex gap-1.5" role="group" aria-label={t('admin:content.library.filter')}>
          {(['all', 'image', 'pdf'] as const).map((value) => (
            <Button
              key={value}
              type="button"
              size="sm"
              variant={filter === value ? 'default' : 'outline'}
              className="h-7 px-3 text-xs"
              aria-pressed={filter === value}
              onClick={() => setFilter(value)}
            >
              {t(`admin:content.library.${value}`)}
            </Button>
          ))}
        </div>

        {error ? <Alert variant="danger">{t('common:error.unexpected')}</Alert> : null}

        {isPending ? (
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
            {Array.from({ length: 8 }, (_, index) => (
              <Skeleton key={index} className="aspect-square rounded-xl" />
            ))}
          </div>
        ) : shown.length === 0 ? (
          <p className="py-8 text-center text-sm text-text-muted">{t('admin:content.library.empty')}</p>
        ) : (
          <ul className="grid max-h-[55vh] grid-cols-3 gap-3 overflow-y-auto pr-1 sm:grid-cols-4">
            {shown.map((asset) => (
              <li key={asset.key}>
                <button
                  type="button"
                  onClick={() => onPick(asset)}
                  title={asset.name}
                  className={cn(
                    'group flex w-full flex-col overflow-hidden rounded-xl border border-border-default text-left',
                    'transition-colors hover:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                  )}
                >
                  <span className="flex aspect-square items-center justify-center bg-surface-sunken">
                    {asset.kind === 'image' ? (
                      <img src={asset.url} alt="" loading="lazy" className="size-full object-cover" />
                    ) : (
                      <FileText className="size-8 text-text-muted" aria-hidden />
                    )}
                  </span>
                  <span className="space-y-0.5 p-2">
                    <span className="block truncate text-xs font-medium">{asset.name}</span>
                    <span className="block text-[11px] text-text-muted">{size(asset.sizeBytes)}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
